import { useEffect, useRef, useState } from "react";
import { X, Tags, Pencil, Trash2, Plus, AlertTriangle } from "lucide-react";
import { useDocuments } from "../modal/DocumentsContext";
import type { SimilarCategoryMatch } from "../api/DocumentsApi";

interface Props {
  onClose: () => void;
}

export default function CategoryManagerModal({ onClose }: Props) {
  const { categories, createCategory, renameCategory, removeCategory, checkSimilarCategories } = useDocuments();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [similar, setSimilar] = useState<SimilarCategoryMatch[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!newName.trim()) { setSimilar([]); return; }
    debounceRef.current = setTimeout(async () => {
      try { setSimilar(await checkSimilarCategories(newName.trim())); } catch { /* non-blocking */ }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [newName, checkSimilarCategories]);

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      await createCategory(name);
      setNewName("");
      setSimilar([]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create category");
    } finally {
      setSaving(false);
    }
  }

  async function handleRename(id: number) {
    const name = editName.trim();
    if (!name) { setEditingId(null); return; }
    try {
      await renameCategory(id, name);
      setEditingId(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to rename category");
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Delete this category? Documents using it will become uncategorized.")) return;
    try {
      await removeCategory(id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to delete category");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <Tags className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">Manage Categories</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-1.5 mb-4 max-h-60 overflow-y-auto">
          {categories.length === 0 && (
            <p className="text-xs text-muted-foreground font-inter">No categories yet.</p>
          )}
          {categories.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-surface-muted/60 border border-border/60 rounded-lg px-3 py-1.5">
              {editingId === c.id ? (
                <input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => handleRename(c.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleRename(c.id); if (e.key === "Escape") setEditingId(null); }}
                  className="flex-1 bg-transparent border-b border-primary text-sm text-foreground font-inter focus:outline-none mr-2"
                />
              ) : (
                <span className="text-xs text-foreground font-inter">{c.name}</span>
              )}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => { setEditingId(c.id); setEditName(c.name); }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(c.id)} className="text-destructive hover:text-destructive/80">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {similar.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-3">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p>Similar category already exists:</p>
              {similar.map((m) => (
                <p key={m.id}><span className="font-medium">{m.name}</span> ({Math.round(m.ratio * 100)}% match)</p>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder="New category name"
            className="flex-1 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
          />
          <button
            onClick={handleAdd}
            disabled={saving || !newName.trim()}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors disabled:opacity-50 font-inter"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>

        {error && (
          <p className="text-xs text-destructive font-inter mt-3 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
